// Summary language resolution, shared by server (cache lookup, summarize) and
// web client (history lookup, settings). Videos spoken in one of the user's
// `keep` languages are summarized in that language, all others in `fallback`.
import type { LangPreference, SummaryLang } from './api.js';

export const SUMMARY_LANGS = ['de', 'en', 'es', 'nl'] as const satisfies readonly SummaryLang[];

export const DEFAULT_LANG_PREFERENCE: LangPreference = { keep: ['de', 'en'], fallback: 'de' };

export function isSummaryLang(value: unknown): value is SummaryLang {
  return typeof value === 'string' && (SUMMARY_LANGS as readonly string[]).includes(value);
}

/** "en-US" -> "en", "pt_BR" -> "pt"; undefined for empty input. */
export function baseLang(code: string | null | undefined): string | undefined {
  const base = code?.trim().toLowerCase().split(/[-_.]/)[0];
  return base || undefined;
}

/** The summary language for a video spoken in `sourceLang` (unknown -> fallback). */
export function resolveSummaryLang(sourceLang: string | null | undefined, pref: LangPreference): SummaryLang {
  const base = baseLang(sourceLang);
  return isSummaryLang(base) && pref.keep.includes(base) ? base : pref.fallback;
}

/**
 * Picks the existing summary (cache rows, history entries) that matches the
 * preference. With a known source language the choice is exact. Summaries
 * created before source languages were recorded: the one in the fallback
 * language, else one in a kept language.
 */
export function pickForPreference<T extends { lang: SummaryLang; sourceLang?: string | null }>(
  candidates: readonly T[],
  pref: LangPreference,
): T | undefined {
  const known = candidates.find((c) => c.sourceLang);
  if (known) {
    const target = resolveSummaryLang(known.sourceLang, pref);
    return candidates.find((c) => c.lang === target);
  }
  return candidates.find((c) => c.lang === pref.fallback) ?? candidates.find((c) => pref.keep.includes(c.lang));
}

/** Validates untrusted input (request bodies, query strings, localStorage); null if invalid. */
export function parseLangPreference(value: unknown): LangPreference | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const { keep, fallback } = value as Record<string, unknown>;
  if (!Array.isArray(keep) || !isSummaryLang(fallback)) return null;
  const langs = SUMMARY_LANGS.filter((lang) => keep.includes(lang));
  return { keep: langs, fallback };
}

/** Query string form: keep=de,en&fallback=de */
export function langPreferenceParams(pref: LangPreference): Record<string, string> {
  return { keep: pref.keep.join(','), fallback: pref.fallback };
}

export function langPreferenceFromParams(keep: string | null | undefined, fallback: string | null | undefined): LangPreference | null {
  if (keep == null || fallback == null) return null;
  return parseLangPreference({ keep: keep.split(',').filter(Boolean), fallback });
}
